import { useState } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { trackEvent, usePageTracking } from "./utils/analytics";
import HomePage from "./components/HomePage";
import HeroSection from "./components/HeroSection";
import RegistrationForm from "./components/RegistrationForm";
import ConfirmationModal from "./components/ConfirmationModal";
import AdminApp from "./components/admin/AdminApp";
import ContactPage from "./components/ContactPage";
import TravelHotelPage from "./components/TravelHotelPage";
import AwardsPage from "./components/AwardsPage";
import AgendaPage from "./components/AgendaPage";
import { tokens } from "./styles/tokens";
import { submitChicagoDCRsvp } from "./api/rsvpApi";
import { sendRsvpConfirmationEmail } from "./integrations/emailClient";

const initialForm = {
  firstName: "",
  lastName: "",
  email: "",
  company: "",
  dinnerSelection: "shortRib",
  dietaryRestrictions: "",
  termsAccepted: false,
};

const dinnerOptions = {
  shortRib: {
    title: "Option 1: Balsamic Glazed Short Rib",
    description: "Butternut Squash, Haricot Vert, Crispy Sweet Potato, Cipollini Onion",
  },
  chicken: {
    title: "Option 2: Herb Roasted Chicken Breast",
    description: "Creamy Polenta, Squash Ratatouille, Lemon Thyme Jus",
  },
  vegetarian: {
    title: "Vegetarian Option: Champagne Superfino Risotto",
    description: "Asparagus, Roasted Corn, Fava Bean, Corn Shoots, Shaved Ricotta Salata",
  },
};

function validateEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value).trim());
}

function RsvpPage() {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  function setField(name, value) {
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
    if (submitError) setSubmitError("");
  }

  function validate(nextForm) {
    const nextErrors = {};

    if (!nextForm.firstName.trim()) nextErrors.firstName = "First name is required.";
    if (!nextForm.lastName.trim()) nextErrors.lastName = "Last name is required.";
    if (!nextForm.email.trim()) nextErrors.email = "Email address is required.";
    if (nextForm.email.trim() && !validateEmail(nextForm.email)) {
      nextErrors.email = "Enter a valid email address.";
    }
    if (!nextForm.company.trim()) nextErrors.company = "Company name is required.";
    if (!nextForm.dinnerSelection) nextErrors.dinnerSelection = "Please select a dinner option.";
    if (!nextForm.dietaryRestrictions.trim()) {
      nextErrors.dietaryRestrictions = "Dietary restrictions are required.";
    }
    if (!nextForm.termsAccepted) nextErrors.termsAccepted = "You must agree to the Terms and Conditions.";

    return nextErrors;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const nextErrors = validate(form);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    setIsSubmitting(true);
    setSubmitError("");

    const dinnerSelectionTitle = dinnerOptions[form.dinnerSelection]?.title ?? "";

    try {
      const response = await submitChicagoDCRsvp({
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email.trim(),
        company: form.company.trim(),
        dinnerSelection: dinnerSelectionTitle || undefined,
        dietaryRestrictions: form.dietaryRestrictions.trim() || undefined,
      });

      const rsvp = response?.rsvp;
      const summary = {
        name: `${rsvp?.firstName ?? form.firstName.trim()} ${rsvp?.lastName ?? form.lastName.trim()}`.trim(),
        email: rsvp?.email ?? form.email.trim(),
        company: rsvp?.company ?? form.company.trim(),
        dietaryRestrictions: rsvp?.dietaryRestrictions ?? form.dietaryRestrictions.trim(),
        dinnerSelection: rsvp?.dinnerSelection ?? dinnerSelectionTitle,
      };

      setSubmitted(summary);
      setIsModalOpen(true);

      trackEvent("form_submit", {
        form_name: "rsvp_registration",
        form_destination: "/rsvp",
      });

      sendRsvpConfirmationEmail({
        firstName: rsvp?.firstName ?? form.firstName.trim(),
        lastName: rsvp?.lastName ?? form.lastName.trim(),
        email: rsvp?.email ?? form.email.trim(),
        company: rsvp?.company ?? form.company.trim(),
        dinnerSelection: rsvp?.dinnerSelection ?? dinnerSelectionTitle,
        dietaryRestrictions: rsvp?.dietaryRestrictions ?? form.dietaryRestrictions.trim(),
      }).catch(() => { });
    } catch (err) {
      setSubmitError(err?.message || "Unable to submit RSVP right now. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  }

  function handleModalClose() {
    setIsModalOpen(false);
    setIsSubmitting(false);
    setSubmitError("");
    setForm(initialForm);
    setErrors({});
    setSubmitted(null);
  }

  return (
    <div className="app" style={{ "--appMaxWidth": tokens.layout.maxWidth }}>
      <HeroSection />
      <RegistrationForm
        form={form}
        errors={errors}
        isSubmitting={isSubmitting}
        submitError={submitError}
        dinnerOptions={dinnerOptions}
        onChange={setField}
        onSubmit={handleSubmit}
      />
      <ConfirmationModal
        open={isModalOpen}
        onClose={handleModalClose}
        summary={submitted}
      />
    </div>
  );
}

function AdminPage() {
  return (
    <div className="app" style={{ "--appMaxWidth": tokens.layout.maxWidth }}>
      <AdminApp />
    </div>
  );
}

function AppRoutes() {
  usePageTracking();

  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/agenda" element={<AgendaPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/awards" element={<AwardsPage />} />
      <Route path="/travel-hotel" element={<TravelHotelPage />} />
      <Route path="/rsvp" element={<RsvpPage />} />
      <Route path="/admin" element={<AdminPage />} />
      <Route path="*" element={<HomePage />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter basename="/chicago">
      <AppRoutes />
    </BrowserRouter>
  );
}
